import { Button as MuiButton, InputAdornment, Stack } from '@mui/material';
import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import SearchIcon from '@/public/icons/search2.svg';
import ArrowIcon from '@/public/icons/arrow02.svg';
import AiArrowSwitch from '@/public/icons/ai-arrow-switch-horizontal.svg';
import DoubleArrowIcon from '@/public/icons/arrow03-right.svg';
import BackwordIcon from '@/public/icons/backword.svg';
import { Body1 } from '@/components/@IntranetLibrary/Typography';
import {
  useWordReplaceHistory,
  useWordReplace,
  useDeleteWordReplaceHistory,
  WordReplaceHistoryItem,
} from '@/hooks/apiHooks/wordReplace';
import EmptyContent from '@/components/@IntranetLibrary/EmptyContent';
import HistoryItem from './HistoryItem';
import SearchResultItem from './SearchResultItem';
import { useTranslation } from 'react-i18next';
import {
  CompactReplaceButton,
  ListTitleWrapper,
  ListWrapper,
  TextFieldWrappers,
} from './style';
import FormProvider from '@/components/HookForm/FormProvider';
import RHFTextField from '@/components/HookForm/RHFTextField';
import useWordReplaceForm from '@/hooks/form/useWordReplaceForm';
import { extractSearchResults } from '@/utils/searchUtils';
import { useDialog } from '@/hooks/global-tools';
import Spinner from '@/components/@IntranetLibrary/Spinner';
import { useDebounce } from 'usehooks-ts';

export type WordReplacePanelProps = {
  closeList?: () => void;
  documentId: string;
  className?: string;
  onSearchTextChange?: (text: string) => void;
  content?: string; // HTML 컨텐츠
  onScrollToResult?: (resultId: string) => void; // 검색 결과 클릭 시 스크롤 콜백
  onHoverResult?: (resultId: string | null) => void; // 검색 결과 hover 시 콜백
  onReplaceContent?: (
    oldText: string,
    newText: string,
    resultId?: string,
  ) => void; // 컨텐츠 바꾸기 콜백
  onClearFocus?: () => void; // 포커스 초기화 콜백
};

/**
 * 단어 바꾸기 패널
 * Gate 1: UI 구조 + RHF 통합
 * - React Hook Form 사용
 * - 검색/치환 로직 없음 (Gate 2)
 * - debounce 없음 (Gate 2)
 * - 호버/클릭 이벤트 없음 (Gate 3)
 */
export default function WordReplacePanel({
  closeList,
  documentId,
  className,
  onSearchTextChange,
  content = '',
  onScrollToResult,
  onHoverResult,
  onReplaceContent,
  onClearFocus,
}: WordReplacePanelProps) {
  const { t } = useTranslation();
  const dialog = useDialog();
  const { data: historyData, mutate: mutateHistory } =
    useWordReplaceHistory(documentId);
  const methods = useWordReplaceForm();
  const { watch, setValue } = methods;
  const findText = watch('findText');
  const replaceText = watch('replaceText');

  const { mutateAsync: replaceWord, isPending } = useWordReplace();
  const { mutateAsync: deleteHistory } = useDeleteWordReplaceHistory();

  // 삭제 중인 히스토리 아이템 추적 (original 값으로)
  const [deletingHistoryId, setDeletingHistoryId] = useState<string | null>(
    null,
  );

  // 호버된 히스토리 아이템 (미리보기용)
  const [hoveredHistory, setHoveredHistory] =
    useState<WordReplaceHistoryItem | null>(null);

  // debounced findText (검색 실행용)
  const debouncedFindText = useDebounce(findText, 300);

  // 검색 결과 계산 (debounced findText 기반)
  const searchResults = useMemo(() => {
    if (!debouncedFindText || debouncedFindText.trim() === '' || !content) {
      return [];
    }
    return extractSearchResults(content, debouncedFindText);
  }, [content, debouncedFindText]);

  // 검색 상태 정의
  type SearchState = 'init' | 'searching' | 'completed';
  const searchState: SearchState = (() => {
    if (!findText || findText.trim() === '') {
      return 'init'; // 검색어 없음 - 최근 변경 이력
    }
    if (findText !== debouncedFindText) {
      return 'searching'; // 검색 중 (debounce 중)
    }
    return 'completed'; // 검색 완료
  })();

  // debouncedFindText 변경 시 부모에게 알림 (실제 검색어와 동기화)
  useEffect(() => {
    onSearchTextChange?.(debouncedFindText);
  }, [debouncedFindText]);

  // 검색 결과 클릭 핸들러
  const handleResultClick = (result: { id: string }) => {
    onScrollToResult?.(result.id);
  };

  // 바꾸기 버튼 클릭 핸들러 (단일)
  const handleReplace = async (result: { id: string }) => {
    if (!debouncedFindText || !replaceText) return;

    // 같은 단어인 경우 경고
    if (debouncedFindText.trim() === replaceText.trim()) {
      dialog.show({
        message: t('WordReplace.찾을 단어와 바꿀 단어가 같습니다.'),
        confirmButtonText: t('WordReplace.확인'),
      });
      return;
    }

    await replaceWord({
      original: debouncedFindText,
      replaced: replaceText,
      count: 1,
    });
    onReplaceContent?.(debouncedFindText, replaceText, result.id);
    mutateHistory(); // 히스토리 갱신
  };

  // 모두 바꾸기 버튼 클릭 핸들러
  const handleReplaceAll = async () => {
    if (!debouncedFindText || !replaceText) return;

    // 같은 단어인 경우 경고
    if (debouncedFindText.trim() === replaceText.trim()) {
      dialog.show({
        message: t('WordReplace.찾을 단어와 바꿀 단어가 같습니다.'),
        confirmButtonText: t('WordReplace.확인'),
      });
      return;
    }

    onClearFocus?.();

    //API 갱신 실패시 업뎃 안되도록 순서 보장
    await replaceWord({
      original: debouncedFindText,
      replaced: replaceText,
      count: searchResults.length,
    });
    onReplaceContent?.(debouncedFindText, replaceText);
    mutateHistory(); // 히스토리 갱신

    // 입력 필드 초기화
    setValue('findText', '');
    setValue('replaceText', '');
  };

  // 검색 결과 hover 핸들러
  const handleHover = (resultId: string | null) => {
    onHoverResult?.(resultId);
  };

  // 히스토리 삭제 핸들러
  const handleDeleteHistory = async (history: WordReplaceHistoryItem) => {
    setDeletingHistoryId(history.original);
    try {
      await deleteHistory({
        original: history.original,
        replaced: history.replaced,
      });
      mutateHistory(); // 히스토리 갱신
    } finally {
      setDeletingHistoryId(null);
    }
  };

  // 히스토리 클릭 핸들러
  const handleHistoryClick = (history: WordReplaceHistoryItem) => {
    setValue('findText', history.original);
    setValue('replaceText', history.replaced);
    setHoveredHistory(null); // 클릭 후 호버 상태 초기화
  };

  // 히스토리 호버 핸들러
  const handleHistoryHover = (history: WordReplaceHistoryItem | null) => {
    setHoveredHistory(history);
  };

  // 공통 스타일
  const emptyContentStyle = { margin: 'auto 0' };
  const searchResultsContainerStyle = { gap: '12px', padding: '12px' };

  // "모두 바꾸기" 버튼
  const renderReplaceAllButton = () => {
    if (replaceText.trim() === '') return null;

    return (
      <CompactReplaceButton
        variant="secondary"
        size="small"
        onClick={handleReplaceAll}
        disabled={isPending}
      >
        {t('WordReplace.모두 바꾸기')}
      </CompactReplaceButton>
    );
  };

  // 초기 상태 렌더링 (최근 변경 이력)
  const renderInitState = () => (
    <>
      <ListTitle
        title={t('WordReplace.최근 변경이력')}
        Icon={<BackwordIcon />}
      />
      {historyData.length === 0 ? (
        <EmptyContent
          title={t('WordReplace.최근 변경한 이력이 없어요')}
          sx={emptyContentStyle}
        />
      ) : (
        <Stack sx={{ padding: '12px' }}>
          {historyData.map((history, idx) => (
            <HistoryItem
              key={idx}
              history={history}
              onDelete={handleDeleteHistory}
              isDeleting={deletingHistoryId === history.original}
              onClick={handleHistoryClick}
              onHover={handleHistoryHover}
            />
          ))}
        </Stack>
      )}
    </>
  );

  // 검색 중 상태 렌더링
  const renderSearchingState = () => (
    <ListTitle
      title={t('WordReplace.조회중...')}
      titleColor="#888D96"
      Icon={<Spinner theme={'ai'} size={'24px'} className={'ai-spinner'} />}
    />
  );

  // 검색 완료 상태 렌더링
  const renderCompletedState = () => (
    <>
      {searchResults.length > 0 ? (
        <>
          <ListTitle
            title={`${searchResults.length}${t('WordReplace.개의 검색 결과')}`}
            actionButton={renderReplaceAllButton()}
          />
          <Stack sx={searchResultsContainerStyle}>
            {searchResults.map((result) => (
              <SearchResultItem
                key={result.id}
                result={result}
                replaceText={replaceText}
                onClick={handleResultClick}
                onReplace={handleReplace}
                onHover={handleHover}
                isReplacing={isPending}
              />
            ))}
          </Stack>
        </>
      ) : (
        <>
          <ListTitle title={`0${t('WordReplace.개의 검색 결과')}`} />
          <EmptyContent
            title={
              <>
                {t('WordReplace.일치하는 단어를 찾지 못했어요.')}
                <br />
                {t('WordReplace.검색어를 확인해 주세요.')}
              </>
            }
            sx={emptyContentStyle}
          />
        </>
      )}
    </>
  );

  // TODO: 공통 패널 기능 및 스타일 합치기
  return (
    <Stack
      className={className}
      sx={{
        height: '100%',
        '& .MuiInputAdornment-root': {
          marginRight: '-4px',
        },
      }}
    >
      {/* 헤더 */}
      {closeList && (
        <MuiButton
          disableRipple
          sx={{
            padding: '12px 20px 12px 8px',
            alignItems: 'center',
            gap: '4px',
            borderBottom: '1px solid var(--dk-line, #E7E9EC)',
            display: 'flex',
            justifyContent: 'flex-start',
            textAlign: 'left',
            borderRadius: '0',
            '&:hover': {
              background: 'initial',
            },
          }}
          onClick={closeList}
        >
          <AiArrowSwitch width={24} height={24} />
          <Body1 sx={{ flex: 1 }}>{t('WordReplace.단어 바꾸기')}</Body1>
          <DoubleArrowIcon />
        </MuiButton>
      )}

      {/* 입력 필드 - FormProvider로 감싸기 */}
      <FormProvider methods={methods} id="WordReplacePanel-Form">
        <TextFieldWrappers>
          <RHFTextField
            name="findText"
            placeholder={
              hoveredHistory
                ? hoveredHistory.original
                : t('WordReplace.찾을 단어를 입력해 주세요.')
            }
            fullWidth
            disabled={isPending}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                sx: hoveredHistory
                  ? {
                      '& input::placeholder': {
                        color: 'var(--dk-text-secondary, #888D96)',
                        opacity: 1,
                      },
                    }
                  : undefined,
              },
            }}
          />
          <RHFTextField
            name="replaceText"
            placeholder={
              hoveredHistory
                ? hoveredHistory.replaced
                : t('WordReplace.바꿀 단어를 입력해 주세요.')
            }
            size="small"
            fullWidth
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <ArrowIcon />
                  </InputAdornment>
                ),
                sx: {
                  '& .MuiInputAdornment-root path': {
                    stroke: isPending ? '#D1D8E2' : 'black',
                  },
                  ...(hoveredHistory && {
                    '& input::placeholder': {
                      color: 'var(--dk-text-secondary, #888D96)',
                      opacity: 1,
                    },
                  }),
                },
              },
            }}
            disabled={isPending}
          />
        </TextFieldWrappers>
      </FormProvider>

      {/* 검색 상태에 따른 UI 표시 */}
      <ListWrapper>
        {searchState === 'init' && renderInitState()}
        {searchState === 'searching' && renderSearchingState()}
        {searchState === 'completed' && renderCompletedState()}
      </ListWrapper>
    </Stack>
  );
}

const ListTitle = ({
  title,
  Icon,
  actionButton,
  titleColor,
}: {
  title: string;
  Icon?: ReactNode;
  actionButton?: ReactNode;
  titleColor?: string;
}) => {
  // title에서 숫자를 찾아서 스타일링
  const renderTitle = () => {
    // 숫자를 찾는 정규식 (괄호 안의 숫자 포함)
    const parts = title.split(/(\d+)/g);

    return (
      <>
        {parts.map((part, index) => {
          // 숫자인 경우
          if (/^\d+$/.test(part)) {
            return (
              <span key={index} style={{ fontWeight: 700, color: 'var(--dk-accent, #0066CC)' }}>
                {part}
              </span>
            );
          }
          // 숫자가 아닌 경우
          return <span key={index}>{part}</span>;
        })}
      </>
    );
  };

  return (
    <ListTitleWrapper
      direction="row"
      justifyContent="space-between"
      alignItems="center"
    >
      <Stack direction="row" alignItems="center" gap="4px">
        {Icon}
        <Body1
          sx={{
            color: titleColor || '#000000',
          }}
        >
          {renderTitle()}
        </Body1>
      </Stack>
      {actionButton}
    </ListTitleWrapper>
  );
};
